import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { PBPUTILS } from "../../config/API";
import jsPDF from 'jspdf';
import SuccessPage from '../SuccessPage';
import appConfig, { getPortalKey } from "../../config";

// Move this function outside the component to prevent re-calculation on every render
const getDefaultMonthYear = () => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth(); // 0-11 (January is 0)
  const currentYear = currentDate.getFullYear();

  let defaultMonth, defaultYear;

  if (currentMonth === 0) { // January
    defaultMonth = 'December';
    defaultYear = (currentYear - 1).toString();
  } else {
    // Get previous month
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    defaultMonth = monthNames[currentMonth - 1];
    defaultYear = currentYear.toString();
  }

  return { defaultMonth, defaultYear };
};

const PacksInventory = () => {
  const [formData, setFormData] = useState({
    // Cigarette Packs section
    line10: '',
    line13: '',
    line14: '0',
    line15: '',
    line18: '',

    // RYO Tobacco section
    line32: '0',
    line35: '',
    line36: '',
    line37: '',
    line39Explanation: '',

    // Tobacco Revenue section
    tobaccoRevenue: {
      giftShop: '',
      nationStation: '',
      vending: ''
    }
  });

  // Add state for shipment locations (for line 11) - START WITH ONLY ONE
  const [shipmentLocations, setShipmentLocations] = useState([
    { id: 1, name: '', value: '', sval: null }
  ]);

  // Add state for RYO locations (for line 33) - START WITH ONLY ONE
  const [ryoLocations, setRyoLocations] = useState([
    { id: 1, name: '', value: '', sval: null }
  ]);

  // Add state to track if line10 is editable
  const [isLine10Editable, setIsLine10Editable] = useState(false);

  // Add state to track if Tobacco Revenue section should be shown
  const [showTobaccoRevenue, setShowTobaccoRevenue] = useState(false);

  // Get business name from localStorage
  const getBusinessName = () => {
    const storedBusinessName = localStorage.getItem("selectedBusinessName");
    return storedBusinessName || 'Potawatomi Retail - One Stop';
  };

  const getExpDate = () => {
    const businessExp = localStorage.getItem("businessLicenseExpirationDate");
    return businessExp || '';
  }

  // Retailer Information State - use function initializer to calculate only once
  const [retailerInfo, setRetailerInfo] = useState(() => {
    const defaultDate = getDefaultMonthYear();
    return {
      retailer: getBusinessName(), // Set dynamically from localStorage
      month: defaultDate.defaultMonth,
      year: defaultDate.defaultYear,
      expirationDate: getExpDate(),
    };
  });

  // Distributors state
  const [distributors, setDistributors] = useState([]);
  const [loadingDistributors, setLoadingDistributors] = useState(true);
  const [loadingStartingCount, setLoadingStartingCount] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [submissionData, setSubmissionData] = useState(null);
  const [attachments, setAttachments] = useState([]);

  // Month options for dropdown
  const monthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Year options (current year and previous years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => (currentYear - 3 + i).toString());

  // Signature pad state and ref - UPDATED: Added new signature fields
  const sigCanvas = useRef(null);
  const [isSigned, setIsSigned] = useState(false);
  const [currentDate] = useState(new Date().toLocaleDateString());
  const [printedName, setPrintedName] = useState('');
  const [title, setTitle] = useState('');
  const [signatureDataURL, setSignatureDataURL] = useState('');
  const [config, setConfig] = useState({});

  // Get taxpayer ID from localStorage
  const taxpayerId = localStorage.getItem("selectedTaxPayer") || '109';

  // Check if Tobacco Revenue section should be shown


  useEffect(() => {
    const key = getPortalKey();
    setConfig(appConfig[key] || appConfig.portal);
  }, []);

  useEffect(() => {
    const checkTobaccoRevenueVisibility = () => {
      if (config.name === "Prairie Band Potawatomi Nation") {
        const businessLicenseType = localStorage.getItem("businessLicenseType");
        const licenseTypes = businessLicenseType ? businessLicenseType.split(',').map(type => type.trim()) : [];

        console.log("licenseTypes");
        console.log(licenseTypes);

        if (licenseTypes.includes("Casino")) {
          setShowTobaccoRevenue(true);
        } else {
          setShowTobaccoRevenue(false);
        }
      } else {
        setShowTobaccoRevenue(false);
      }
      console.log("showTobaccoRevenue");
      console.log(showTobaccoRevenue);
    };

    checkTobaccoRevenueVisibility();
  }, [config]); // Add config as dependency here

  // Format month for API (convert to number with leading zero)
  const formatMonthForAPI = (monthName) => {
    const monthMap = {
      'January': '01', 'February': '02', 'March': '03', 'April': '04',
      'May': '05', 'June': '06', 'July': '07', 'August': '08',
      'September': '09', 'October': '10', 'November': '11', 'December': '12'
    };
    return monthMap[monthName] || '01';
  };

  // Calculate tax amounts for tobacco revenue
  const calculateTaxAmount = (revenue) => {
    const amount = parseFloat(revenue) || 0;
    return (amount * 0.06).toFixed(2);
  };

  // Handle tobacco revenue changes
  const handleTobaccoRevenueChange = (field, value) => {
    // Allow only numbers and decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');
    // Ensure only one decimal point
    const parts = numericValue.split('.');
    const formattedValue = parts.length > 1 ? parts[0] + '.' + parts[1] : parts[0];

    setFormData(prev => ({
      ...prev,
      tobaccoRevenue: {
        ...prev.tobaccoRevenue,
        [field]: formattedValue
      }
    }));
  };

  // Calculate tobacco revenue totals
  const calculateTobaccoTotals = () => {
    const { giftShop, nationStation, vending } = formData.tobaccoRevenue;

    const totalRevenue = (parseFloat(giftShop) || 0) +
      (parseFloat(nationStation) || 0) +
      (parseFloat(vending) || 0);

    const totalTax = (parseFloat(calculateTaxAmount(giftShop)) || 0) +
      (parseFloat(calculateTaxAmount(nationStation)) || 0) +
      (parseFloat(calculateTaxAmount(vending)) || 0);

    return { totalRevenue, totalTax };
  };

  // Fetch starting count from API for line10
  const fetchStartingCount = async () => {
    // Don't fetch if taxpayer ID is not available
    if (!taxpayerId) {
      console.warn('Taxpayer ID not available, skipping API call');
      return;
    }

    try {
      setLoadingStartingCount(true);

      const formattedMonth = formatMonthForAPI(retailerInfo.month);

      // API URL for retailer packs inventory
      const apiUrl = PBPUTILS + `?taxpayer=${taxpayerId}&date=${formattedMonth}-${retailerInfo.year}&reporttype=packs&action=getStartCount`;

      console.log('Fetching starting count from:', apiUrl);

      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.text();
      console.log('API Response:', data);

      // Handle cases where API returns "0" or "0,0"
      if (data.trim() === '0' || data.trim() === '0,0') {
        console.log('API returned zero values, setting line10 to 0');
        setFormData(prev => ({
          ...prev,
          line10: '0'
        }));
        // Make line10 editable when API returns 0
        setIsLine10Editable(true);
      } else {
        // Split the response by comma to get both values
        const values = data.split(',');

        if (values.length >= 1) {
          const line10Value = parseInt(values[0].trim(), 10);

          if (!isNaN(line10Value)) {
            setFormData(prev => ({
              ...prev,
              line10: line10Value.toString()
            }));

            // Make line10 editable only if the value is 0
            if (line10Value === 0) {
              setIsLine10Editable(true);
            } else {
              setIsLine10Editable(false);
            }

            console.log('Successfully loaded starting count - line10:', line10Value);
          } else {
            console.warn('Invalid count value received:', values[0]);
            // Set to 0 if invalid response and make editable
            setFormData(prev => ({
              ...prev,
              line10: '0'
            }));
            setIsLine10Editable(true);
          }
        } else {
          console.warn('Unexpected response format:', data);
          // Set to 0 if unexpected format and make editable
          setFormData(prev => ({
            ...prev,
            line10: '0'
          }));
          setIsLine10Editable(true);
        }
      }
    } catch (error) {
      console.error('Error fetching starting count:', error);
      // Set to 0 on error and make editable
      setFormData(prev => ({
        ...prev,
        line10: '0'
      }));
      setIsLine10Editable(true);
    } finally {
      setLoadingStartingCount(false);
    }
  };

  // Fetch distributors from API
  useEffect(() => {
    const fetchDistributors = async () => {
      try {
        setLoadingDistributors(true);
        const apiUrl = PBPUTILS + `?taxpayer=${taxpayerId}&action=getdistributors`;

        const response = await fetch(apiUrl);

        if (!response.ok) {
          throw new Error('Failed to fetch distributors');
        }

        const data = await response.json();

        if (data.status === 'success') {
          setDistributors(data.data);
        } else {
          throw new Error(data.message || 'Failed to fetch distributors');
        }
      } catch (err) {
        console.error('Error fetching distributors:', err);
        setError(err.message);
        // Set default distributors in case of error
        setDistributors([]);
      } finally {
        setLoadingDistributors(false);
      }
    };

    fetchDistributors();
  }, [taxpayerId]);

  // Fetch starting count when month, year, or taxpayer ID changes
  useEffect(() => {
    if (taxpayerId && retailerInfo.month && retailerInfo.year) {
      fetchStartingCount();
    }
  }, [retailerInfo.month, retailerInfo.year, taxpayerId]);

  // Signature functions - UPDATED
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setIsSigned(false);
      setSignatureDataURL('');
    }
  };

  const handleSignatureBegin = () => {
    setIsSigned(true);
  };

  const handleSignatureEnd = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setIsSigned(true);
      const sigDataURL = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
      setSignatureDataURL(sigDataURL);
    } else {
      setIsSigned(false);
      setSignatureDataURL('');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const nextValue =
      e.target.type === 'number' && value !== ''
        ? String(Math.max(0, Number(value)))
        : value;
    setFormData(prev => ({
      ...prev,
      [name]: nextValue
    }));
  };

  // Handle retailer information changes
  const handleRetailerInfoChange = (field, value) => {
    setRetailerInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle shipment location changes (for line 11)
  const handleShipmentLocationChange = (id, value) => {
    const nextValue =
      value === '' ? '' : String(Math.max(0, Number(String(value).replace(/,/g, ''))));
    setShipmentLocations(prev =>
      prev.map(location =>
        location.id === id ? { ...location, value: nextValue } : location
      )
    );
  };

  const handleShipmentNameChange = (id, selectedName) => {
    const selectedOption = distributors.find(distributor => distributor.dval === selectedName);
    setShipmentLocations(prev =>
      prev.map(location =>
        location.id === id ? {
          ...location,
          name: selectedName,
          sval: selectedOption ? selectedOption.sval : null
        } : location
      )
    );
  };

  const addShipmentLocation = () => {
    const newId = Math.max(...shipmentLocations.map(loc => loc.id), 0) + 1;
    setShipmentLocations(prev => [
      ...prev,
      { id: newId, name: '', value: '', sval: null }
    ]);
  };

  const removeShipmentLocation = (id) => {
    if (shipmentLocations.length > 1) {
      setShipmentLocations(prev => prev.filter(location => location.id !== id));
    }
  };

  // Handle RYO location changes (for line 33)
  const handleRyoLocationChange = (id, value) => {
    const nextValue =
      value === '' ? '' : String(Math.max(0, Number(String(value).replace(/,/g, ''))));
    setRyoLocations(prev =>
      prev.map(location =>
        location.id === id ? { ...location, value: nextValue } : location
      )
    );
  };

  const handleRyoNameChange = (id, selectedName) => {
    const selectedOption = distributors.find(distributor => distributor.dval === selectedName);
    setRyoLocations(prev =>
      prev.map(location =>
        location.id === id ? {
          ...location,
          name: selectedName,
          sval: selectedOption ? selectedOption.sval : null
        } : location
      )
    );
  };

  const addRyoLocation = () => {
    const newId = Math.max(...ryoLocations.map(loc => loc.id), 0) + 1;
    setRyoLocations(prev => [
      ...prev,
      { id: newId, name: '', value: '', sval: null }
    ]);
  };

  const removeRyoLocation = (id) => {
    if (ryoLocations.length > 1) {
      setRyoLocations(prev => prev.filter(location => location.id !== id));
    }
  };

  // File upload handlers for Supporting Documents
  const handleAttachmentChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    setAttachments((prev) => {
      const existingNames = new Set(prev.map((f) => f.name));
      const newFiles = files.filter((f) => !existingNames.has(f.name));
      return [...prev, ...newFiles];
    });

    event.target.value = '';
  };

  const handleRemoveAttachment = (indexToRemove) => {
    setAttachments((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  // Get available options for dropdown (options not already selected)
  const getAvailableOptions = (currentLocationId, locations) => {
    const usedNames = locations
      .filter(location => location.id !== currentLocationId)
      .map(location => location.name);

    return distributors.filter(distributor => !usedNames.includes(distributor.dval));
  };

  // Calculate Line 11 as sum of all shipment locations
  const calculateLine11 = () => {
    return shipmentLocations.reduce((sum, location) => {
      const value = parseInt(location.value) || 0;
      return sum + value;
    }, 0);
  };

  // Calculate Line 12 as sum of line 10 and line 11
  const calculateLine12 = () => {
    const line10 = parseFloat(formData.line10) || 0;
    const line11 = calculateLine11();
    return line10 + line11;
  };

  // Calculate Line 16
  const calculateLine16 = () => {
    const line13 = parseFloat(formData.line13) || 0;
    const line14 = parseFloat(formData.line14) || 0;
    const line15 = parseFloat(formData.line15) || 0;
    return line13 + line14 + line15;
  };

  // Calculate Line 17
  const calculateLine17 = () => {
    const line12 = calculateLine12();
    const line16 = calculateLine16();
    return line12 - line16;
  };

  // RYO Calculations
  const calculateLine33 = () => {
    return ryoLocations.reduce((sum, location) => {
      const value = parseInt(location.value) || 0;
      return sum + value;
    }, 0);
  };

  const calculateLine34 = () => {
    const line32 = parseFloat(formData.line32) || 0;
    const line33 = calculateLine33();
    return line32 + line33;
  };

  const calculateLine38 = () => {
    const line35 = parseFloat(formData.line35) || 0;
    const line36 = parseFloat(formData.line36) || 0;
    const line37 = parseFloat(formData.line37) || 0;
    return line35 + line36 + line37;
  };

  const calculateLine39 = () => {
    const line34 = calculateLine34();
    const line38 = calculateLine38();
    return line34 - line38;
  };

  // Format number to avoid NaN display
  const formatNumber = (value) => {
    return isNaN(value) ? 0 : value;
  };

    const formatAmount = (value) => {
      if (value === null || value === undefined) return '0';
      const s = String(value).replace(/,/g, '').trim();
      if (s === '') return '0';
      const num = Number(s);
      if (isNaN(num)) return String(value);
      if (s.includes('.')) {
        const decimals = s.split('.')[1].length;
        return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
      }
      return num.toLocaleString();
    };

  // Generate dynamic calculation description
  const generateCalculationDescription = (prefix, locations) => {
    const nonEmptyLocations = locations.filter(loc => loc.name && (loc.value || loc.value === '0'));
    if (nonEmptyLocations.length === 0) {
      return 'No distributors selected';
    }
    return nonEmptyLocations.map(loc => loc.name).join(' + ');
  };

  // Helper function to convert base64 data URL to File object
  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  };

  // Add this helper function to convert PDF to Blob
  const pdfToBlob = (pdf) => {
    const pdfOutput = pdf.output('blob');
    return pdfOutput;
  };

  // Generate PDF function with fixed space utilization - accepts optional signature override (use when generating for send, since setState is async)
  const generatePDF = (signatureDataURLOverride) => {
    const pdf = new jsPDF();
    const signatureToUse = signatureDataURLOverride !== undefined ? signatureDataURLOverride : signatureDataURL;
    const pageWidth = pdf.internal.pageSize.getWidth();

    // Set font and colors
    pdf.setFont('helvetica');
    pdf.setTextColor(0, 0, 0); // #840028

    // Header Section
    pdf.setFontSize(16);
    pdf.text('Monthly Retailer Tobacco Report', pageWidth / 2, 20, { align: 'center' });

    pdf.setFontSize(12);
    pdf.text('Prairie Band Potawatomi Tax Commission', pageWidth / 2, 30, { align: 'center' });
    pdf.text('16281 Q Road', pageWidth / 2, 36, { align: 'center' });
    pdf.text('Mayetta, KS 66509', pageWidth / 2, 42, { align: 'center' });

    pdf.setFontSize(10);
    pdf.text('Reports and Payment due on the 10th of each Month', pageWidth / 2, 50, { align: 'center' });

    // Retailer Information
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Retailer Information:', 20, 65);
    pdf.setFontSize(10);
    pdf.text(`Retailer: ${retailerInfo.retailer}`, 20, 72);
    pdf.text(`Month: ${retailerInfo.month} ${retailerInfo.year}`, 20, 78);
    pdf.text(`PBPN Tribal Tobacco Retailer License Expiration Date: ${retailerInfo.expirationDate}`, 20, 84);
    pdf.text(`Taxpayer ID: ${taxpayerId}`, 20, 90);

    let yPosition = 110;

    // Cigarette Packs Section
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Tobacco Retailer - Packs of Cigarettes', 20, yPosition);
    yPosition += 15;

    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);

    // Cigarette Packs Data with detailed descriptions
    const cigaretteData = [
      {
        line: '10',
        label: 'Beginning 1st of the month Inventory of Stamped cigarette packs (last month\'s line 17)',
        value: formData.line10 || '0',
        description: 'Starting inventory from previous month'
      },
      {
        line: '11',
        label: 'STAMPED Cigarette packs received/purchased during the month',
        value: formatNumber(calculateLine11()).toString(),
        description: 'Total packs received from all distributors'
      }
    ];

    // Add distributor details for line 11
    shipmentLocations.forEach((location, index) => {
      if (location.name && (location.value || location.value === '0')) {
        const letter = String.fromCharCode(97 + index);
        cigaretteData.push({
          line: `11${letter}`,
          label: `   ${letter}. ${location.name}`,
          value: location.value || '0',
          description: `Packs received from ${location.name}`
        });
      }
    });

    // Add remaining cigarette data
    cigaretteData.push(
      {
        line: '12',
        label: 'Total STAMPED Cigarette packs available (line 10 + line 11)',
        value: formatNumber(calculateLine12()).toString(),

      },
      {
        line: '13',
        label: 'STAMPED Cigarette packs sold during the month',
        value: formData.line13 || '0',

      },
      {
        line: '14',
        label: 'STAMPED cigarette packs destroyed or returned to distributor/Tax Commission',
        value: formData.line14 || '0',

      },
      {
        line: '15',
        label: 'Un-explained variance in inventory',
        value: formData.line15 || '0',

      },
      {
        line: '16',
        label: 'Total STAMPED Cigarette removed from inventory (Line 13 + Line 14 + Line 15)',
        value: formatNumber(calculateLine16()).toString(),

      },
      {
        line: '17',
        label: 'End of month STAMPED cigarette packs Inventory (Line 12 - Line 16)',
        value: formatNumber(calculateLine17()).toString(),

      }
    );

    // Display cigarette data
    cigaretteData.forEach((item) => {
      if (yPosition > 270) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.text(`${item.line}.`, 20, yPosition);

      // Split long labels into multiple lines
      const labelLines = pdf.splitTextToSize(item.label, 120);
      const valueLines = pdf.splitTextToSize(formatAmount(item.value), 30);

      const maxLines = Math.max(labelLines.length, valueLines.length);

      for (let i = 0; i < maxLines; i++) {
        if (yPosition > 270) {
          pdf.addPage();
          yPosition = 20;
        }

        if (labelLines[i]) {
          pdf.text(labelLines[i], 30, yPosition);
        }

        if (valueLines[i]) {
          pdf.text(valueLines[i], 180, yPosition, { align: 'right' });
        }

        yPosition += 6;
      }

      // Add description in smaller font
      if (item.description) {
        const descLines = pdf.splitTextToSize(`   ${item.description}`, 150);
        descLines.forEach(descLine => {
          if (yPosition > 270) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.setFontSize(8);
          pdf.setTextColor(0, 0, 0);
          pdf.text(descLine, 30, yPosition);
          yPosition += 4;
          pdf.setFontSize(10);
          pdf.setTextColor(0, 0, 0);
        });
      }

      yPosition += 2;
    });

    // Add line 18 explanation if provided
    if (formData.line18 && formData.line18.trim() !== '') {
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.text('18. Explanation of packs removed in line 14 and 15:', 20, yPosition);
      yPosition += 8;

      const explanationLines = pdf.splitTextToSize(formData.line18, 170);
      explanationLines.forEach(line => {
        if (yPosition > 270) {
          pdf.addPage();
          yPosition = 20;
        }
        pdf.text(`   ${line}`, 25, yPosition);
        yPosition += 6;
      });
    }

    // DEBUG: Check current yPosition after cigarette section
    console.log('Position after cigarette section:', yPosition);

    // RYO Tobacco Section - FIXED: Always try to start on current page if there's any space
    // Check if we have ANY space left on current page (more than 30 units)
    if (yPosition < 250) { // If we're above 250, there's at least 30 units of space
      // Add minimal spacing and start RYO section on current page
      yPosition += 8; // Very small spacing between sections

      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Tobacco Retailer - Roll-Your-Own (RYO) Tobacco', 20, yPosition);
      yPosition += 15;

      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);

      console.log('Starting RYO section on page 1 at position:', yPosition);
    } else {
      // Start RYO on new page
      pdf.addPage();
      yPosition = 20;

      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Tobacco Retailer - Roll-Your-Own (RYO) Tobacco', 20, yPosition);
      yPosition += 15;

      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);

      console.log('Starting RYO section on new page');
    }

    // RYO Tobacco Data with detailed descriptions
    const ryoData = [
      {
        line: '32',
        label: 'Beginning 1st of the month Inventory of RYO tobacco ounces (last month\'s line 39)',
        value: formData.line32 || '0',
      },
      {
        line: '33',
        label: 'RYO tobacco ounces received during the month',
        value: formatNumber(calculateLine33()).toString(),
      }
    ];

    // Add distributor details for line 33
    ryoLocations.forEach((location, index) => {
      if (location.name && (location.value || location.value === '0')) {
        const letter = String.fromCharCode(97 + index);
        ryoData.push({
          line: `33${letter}`,
          label: `   ${letter}. ${location.name}`,
          value: location.value || '0',
          description: `RYO ounces received from ${location.name}`
        });
      }
    });

    // Add remaining RYO data
    ryoData.push(
      {
        line: '34',
        label: 'Total RYO ounces available for sale (Lines 32 + Line 33)',
        value: formatNumber(calculateLine34()).toString(),
      },
      {
        line: '35',
        label: 'RYO ounces sold during the month',
        value: formData.line35 || '0',
      },
      {
        line: '36',
        label: 'RYO ounces destroyed or returned to distributor or manufacturer',
        value: formData.line36 || '0',
      },
      {
        line: '37',
        label: 'Adjustment (Please Explain)',
        value: formData.line37 || '0',
      },
      {
        line: '38',
        label: 'Total RYO ounces removed from inventory (Line 35 + 36 + 37)',
        value: formatNumber(calculateLine38()).toString(),
      },
      {
        line: '39',
        label: 'End of month "Calculated" RYO Inventory (Line 34 - Line 38)',
        value: formatNumber(calculateLine39()).toString(),
      }
    );

    // Display RYO data
    ryoData.forEach((item) => {
      if (yPosition > 270) {
        pdf.addPage();
        yPosition = 20;

        // If we're on a new page and this is early RYO data, add section header again
        if (item.line === '32' || item.line === '33' || item.line === '34') {
          pdf.setFontSize(12);
          pdf.setTextColor(0, 0, 0);
          pdf.text('Tobacco Retailer - Roll-Your-Own (RYO) Tobacco', 20, yPosition);
          yPosition += 15;
          pdf.setFontSize(10);
          pdf.setTextColor(0, 0, 0);
        }
      }

      pdf.text(`${item.line}.`, 20, yPosition);

      // Split long labels into multiple lines
      const labelLines = pdf.splitTextToSize(item.label, 120);
      const valueLines = pdf.splitTextToSize(formatAmount(item.value), 30);

      const maxLines = Math.max(labelLines.length, valueLines.length);

      for (let i = 0; i < maxLines; i++) {
        if (yPosition > 270) {
          pdf.addPage();
          yPosition = 20;
        }

        if (labelLines[i]) {
          pdf.text(labelLines[i], 30, yPosition);
        }

        if (valueLines[i]) {
          pdf.text(valueLines[i], 180, yPosition, { align: 'right' });
        }

        yPosition += 6;
      }

      // Add description in smaller font
      if (item.description) {
        const descLines = pdf.splitTextToSize(`   ${item.description}`, 150);
        descLines.forEach(descLine => {
          if (yPosition > 270) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.setFontSize(8);
          pdf.setTextColor(0, 0, 0);
          pdf.text(descLine, 30, yPosition);
          yPosition += 4;
          pdf.setFontSize(10);
          pdf.setTextColor(0, 0, 0);
        });
      }

      yPosition += 2;
    });

    // Add RYO explanation if provided
    if (formData.line39Explanation && formData.line39Explanation.trim() !== '') {
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.text('Explanation of RYO ounces removed in line 36:', 20, yPosition);
      yPosition += 8;

      const explanationLines = pdf.splitTextToSize(formData.line39Explanation, 170);
      explanationLines.forEach(line => {
        if (yPosition > 270) {
          pdf.addPage();
          yPosition = 20;
        }
        pdf.text(`   ${line}`, 25, yPosition);
        yPosition += 6;
      });
    }

    // Tobacco Revenue Section in PDF (only if showTobaccoRevenue is true)
    if (showTobaccoRevenue) {
      if (yPosition > 200) {
        pdf.addPage();
        yPosition = 20;
      } else {
        yPosition += 15;
      }

      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Tobacco Revenue', 20, yPosition);
      yPosition += 10;

      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);

      // Tobacco Revenue Table
      const revenueData = [
        {
          label: '1. Gift Shop - Tobacco Revenue',
          revenue: formData.tobaccoRevenue.giftShop || '0.00',
          taxRate: '6%',
          taxAmount: calculateTaxAmount(formData.tobaccoRevenue.giftShop)
        },
        {
          label: '2. Nation Station - Tobacco Revenue',
          revenue: formData.tobaccoRevenue.nationStation || '0.00',
          taxRate: '6%',
          taxAmount: calculateTaxAmount(formData.tobaccoRevenue.nationStation)
        },
        {
          label: '3. Vending - Tobacco Revenue',
          revenue: formData.tobaccoRevenue.vending || '0.00',
          taxRate: '6%',
          taxAmount: calculateTaxAmount(formData.tobaccoRevenue.vending)
        },
        {
          label: 'Total Tobacco',
          revenue: calculateTobaccoTotals().totalRevenue.toFixed(2),
          taxRate: '6%',
          taxAmount: calculateTobaccoTotals().totalTax.toFixed(2)
        }
      ];

      revenueData.forEach((item, index) => {
        if (yPosition > 270) {
          pdf.addPage();
          yPosition = 20;
        }

        const isTotal = index === revenueData.length - 1;

        if (isTotal) {
          pdf.setFont('helvetica', 'bold');
          yPosition += 5; // Add space before total row
        }

        // Label
        pdf.text(item.label, 20, yPosition);

        // Revenue Amount
        pdf.text(`$${formatAmount(item.revenue)}`, 120, yPosition, { align: 'right' });

        // Tax Rate
        pdf.text(item.taxRate, 150, yPosition, { align: 'right' });

        // Tax Amount
        pdf.text(`$${formatAmount(item.taxAmount)}`, 180, yPosition, { align: 'right' });

        if (isTotal) {
          pdf.setFont('helvetica', 'normal');
        }

        yPosition += 8;
      });
    }

    // Signature and Certification Section
    if (yPosition > 160) {
      pdf.addPage();
      yPosition = 20;
    } else {
      yPosition += 15;
    }

    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Signature and Certification', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 12;

    // Certification Text
    pdf.setFontSize(10);
    pdf.text('I certify that I am a duly authorized owner, partner, director, officer or', 20, yPosition);
    yPosition += 5;
    pdf.text('representative of the licensee and that the statements made herein are true', 20, yPosition);
    yPosition += 5;
    pdf.text('and correct to the best of my knowledge.', 20, yPosition);
    yPosition += 8;

    // Add signature image if available
    if (signatureToUse) {
      try {
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        pdf.text('Signature:', 20, yPosition);
        yPosition += 6;

        // Add signature image to PDF
        const signatureWidth = 80;
        const signatureHeight = 30;

        // Check if there's enough space for signature
        if (yPosition + signatureHeight > 270) {
          pdf.addPage();
          yPosition = 20;
          pdf.text('Signature:', 20, yPosition);
          yPosition += 6;
        }

        pdf.addImage(signatureToUse, 'PNG', 20, yPosition, signatureWidth, signatureHeight);
        yPosition += signatureHeight + 10;
      } catch (error) {
        console.error('Error adding signature to PDF:', error);
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        pdf.text('Signature: Provided during submission', 20, yPosition);
        yPosition += 8;
      }
    } else {
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Signature: Not provided', 20, yPosition);
      yPosition += 8;
    }

    // Printed Name and Title
    yPosition += 3;
    pdf.text(`Printed Name: ${printedName}`, 20, yPosition);
    yPosition += 5;
    pdf.text(`Title: ${title}`, 20, yPosition);
    yPosition += 6;

    // Date Signed
    pdf.text(`Date Signed: ${currentDate}`, 20, yPosition);

    return pdf;
  };

  // Helper function to convert base64 data URL to blob
  const dataURLtoBlob = (dataurl) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  // Function to handle PDF download from SuccessPage
  const handleDownloadPDF = () => {
    const pdf = generatePDF();
    const fileName = `PacksInventory_Report_${retailerInfo.month}_${retailerInfo.year}_${taxpayerId || 'unknown'}.pdf`;
    pdf.save(fileName);
  };

  // Function to handle back to form
  const handleBackToForm = () => {
    setShowSuccessScreen(false);
    // Reset form or navigate as needed
  };

  // Get business name for SuccessPage
  const businessName = getBusinessName();

  // API submission function - UPDATED: Added PDF to formData
  const submitFormData = async (submissionData) => {
    try {
      setSubmitting(true);

      // Format month for API
      const formattedMonth = formatMonthForAPI(submissionData.month);
      const dateParam = `${formattedMonth}-${submissionData.year}`;

      // Generate PDF with signature passed in (state update is async, so state would still be old here)
      const pdf = generatePDF(submissionData.signature);
      const pdfBlob = pdfToBlob(pdf);
      const pdfFile = new File([pdfBlob], `PacksInventory_Report_${submissionData.month}_${submissionData.year}_${taxpayerId}.pdf`, {
        type: 'application/pdf'
      });

      // Create the JSON data structure as specified
      const reportData = {
        perpack: "20s", // Assuming standard pack size
        received: [],
        beginningInventory: parseInt(submissionData.line10) || 0,
        line13: parseInt(submissionData.line13) || 0,
        line14: parseInt(submissionData.line14) || 0,
        line15: parseInt(submissionData.line15) || 0,
        line18: submissionData.line18 || '',
        ouncesreceived: [],
        beginningInventoryRYO: parseInt(submissionData.line32) || 0,
        line35: parseInt(submissionData.line35) || 0,
        line36: parseInt(submissionData.line36) || 0,
        line37: parseInt(submissionData.line37) || 0,
        line39Explanation: submissionData.line39Explanation || '',
        printedName: submissionData.printedName || '',
        title: submissionData.title || ''
      };

      // Add tobacco revenue data only if section is shown
      if (showTobaccoRevenue) {
        reportData.tobaccoRevenue = submissionData.tobaccoRevenue || {};
      }

      // Process cigarette packs received from shipment locations
      shipmentLocations.forEach((location) => {
        if (location.name && location.sval) {
          const packsValue = parseInt(location.value) || 0;
          if (packsValue > 0) {
            reportData.received.push({
              distributor: location.sval,
              packs: packsValue
            });
          }
        }
      });

      // Process RYO ounces received from ryo locations
      ryoLocations.forEach((location) => {
        if (location.name && location.sval) {
          const ouncesValue = parseInt(location.value) || 0;
          if (ouncesValue > 0) {
            reportData.ouncesreceived.push({
              distributor: location.sval,
              ounces: ouncesValue
            });
          }
        }
      });

      // Store submission data for SuccessPage
      setSubmissionData({
        formData,
        retailerInfo,
        distributors,
        calculations: {
          line11: calculateLine11(),
          line12: calculateLine12(),
          line16: calculateLine16(),
          line17: calculateLine17(),
          line33: calculateLine33(),
          line34: calculateLine34(),
          line38: calculateLine38(),
          line39: calculateLine39(),
          tobaccoTotals: showTobaccoRevenue ? calculateTobaccoTotals() : null
        },
        printedName: submissionData.printedName,
        title: submissionData.title
      });

      // Create FormData object
      const formDataToSend = new FormData();

      // Add the report data as JSON string
      formDataToSend.append('Body', JSON.stringify(reportData));

      console.log('Report data being sent:', JSON.stringify(reportData));


      // Add signature as blob
      if (submissionData.signature) {
        const signatureBlob = dataURLtoBlob(submissionData.signature);
        formDataToSend.append('Signature', signatureBlob, 'signature.png');
      }

      // Add PDF to FormData - UPDATED: Same as UnstampedPacks
      formDataToSend.append('PDF', pdfFile);

      // Append any optional supporting document uploads (same as StampsInventory)
      attachments.forEach((file) => {
        formDataToSend.append('Receipts', file);
      });

      // Add printed name and title
      formDataToSend.append('printedName', submissionData.printedName);
      formDataToSend.append('title', submissionData.title);

      // Construct API URL with query parameters
      const apiUrl = PBPUTILS + `?taxpayer=${taxpayerId}&reporttype=packs&date=${dateParam}&test=true`;

      console.log('Submitting to:', apiUrl);
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseText = await response.text();
      console.log('API Response:', responseText);

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.warn('Response is not valid JSON, treating as plain text:', responseText);
        // Handle non-JSON responses (like "true" or "false")
        if (responseText.trim().toLowerCase() === 'true') {
          // Show success screen for successful submission
          setShowSuccessScreen(true);
          return {
            success: true,
            message: 'Packs inventory report submitted successfully!'
          };
        } else {
          throw new Error(`Unexpected response from server: ${responseText}`);
        }
      }

      // Handle JSON response
      if (result && result.Status === 'Success') {
        setShowSuccessScreen(true);
        return {
          success: true,
          message: 'Packs inventory report submitted successfully!'
        };
      } else if (result && result.Status === 'Error') {
        // Show the specific error message from the API

        return {
          success: false,
          message: result.Message || 'An error occurred while submitting the report.'
        };
      } else {
        throw new Error('Unexpected response format from server');
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      alert(`Failed to submit report: ${error.message}`);
      return {
        success: false,
        message: `Failed to submit report: ${error.message}`
      };
    } finally {
      setSubmitting(false);
    }
  };

  // UPDATED: Handle submit with SuccessPage
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isSigned || (sigCanvas.current && sigCanvas.current.isEmpty())) {
      alert('Please provide a signature before submitting the report.');
      return;
    }

    // Add validation for printed name and title
    if (!printedName.trim()) {
      alert('Please enter your printed name.');
      return;
    }

    if (!title.trim()) {
      alert('Please enter your title.');
      return;
    }

    const signatureData = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
    setSignatureDataURL(signatureData);

    const submissionData = {
      ...formData,
      ...retailerInfo,
      signature: signatureData,
      signatureDate: currentDate,
      printedName: printedName.trim(),
      title: title.trim()
    };

    console.log('Form data to submit:', submissionData);

    // Call the API submission function
    const result = await submitFormData(submissionData);

    if (result.success) {
      // Show SuccessPage instead of modal
      setShowSuccessScreen(true);
    } else {
      alert(`${result.message}`);
    }
  };

  // Render distributor fields dynamically with dropdowns and add/remove buttons
  const renderDistributorFields = (locations, setLocations, handleNameChange, handleValueChange, addLocation, removeLocation, fieldType) => {
    return (
      <div className="space-y-3">
        {locations.map((location, index) => {
          const letter = String.fromCharCode(97 + index);
          const availableOptions = getAvailableOptions(location.id, locations);

          return (
            <div key={location.id} className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 items-center ml-0 sm:ml-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <label className="text-xs sm:text-sm text-gray-700">
                  {fieldType === 'cigarette' ? `11${letter}.` : `33${letter}.`}
                </label>
                <select
                  value={location.name}
                  onChange={(e) => handleNameChange(location.id, e.target.value)}
                  className="border border-gray-300 px-2 sm:px-3 py-1 sm:py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm w-full"
                  disabled={loadingDistributors}
                >
                  {loadingDistributors ? (
                    <option>Loading distributors...</option>
                  ) : (
                    <>
                      <option value="">Select a distributor</option>
                      {availableOptions.map(option => (
                        <option key={option.sval} value={option.dval}>
                          {option.dval}
                        </option>
                      ))}
                      {!availableOptions.some(opt => opt.dval === location.name) && location.name && (
                        <option value={location.name} disabled>
                          {location.name} (currently selected)
                        </option>
                      )}
                    </>
                  )}
                </select>
              </div>
              <div></div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  value={location.value}
                  onChange={(e) => handleValueChange(location.id, e.target.value)}
                  className="border border-gray-300 text-right px-2 sm:px-3 py-1 sm:py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm w-full"
                  placeholder="0"
                />
                {locations.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeLocation(location.id)}
                    className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition duration-200 text-xs"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {/* Add More Button */}
        <div className="flex justify-start ml-4">
          <button
            type="button"
            onClick={addLocation}
            className="flex items-center gap-2 px-3 py-2 bg-[#840028] text-white rounded hover:bg-[#6a0020] transition duration-200 text-sm"
            disabled={loadingDistributors || locations.length >= distributors.length}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Another Distributor
          </button>
        </div>
      </div>
    );
  };

  // Prepare report details for SuccessPage
  const reportDetails = {
    'Report Type': 'Tobacco Retailer - Packs of Cigarettes',
    'Taxpayer ID': taxpayerId,
    'Business': businessName,
    'Period': `${retailerInfo.month} ${retailerInfo.year}`,
    'Submitted': currentDate,
    'License Expiration': retailerInfo.expirationDate
  };

  // If showing success screen, render the SuccessPage component
  if (showSuccessScreen) {
    return (
      <SuccessPage
        title="Packs Inventory Report Submitted Successfully!"
        subtitle={`Your packs inventory report for ${retailerInfo.month} ${retailerInfo.year} has been successfully submitted.`}
        downloadButtonText="Download Packs Inventory Report PDF"
        backButtonText="Back to Home"
        onDownload={handleDownloadPDF}
        onBack={handleBackToForm}
        reportDetails={reportDetails}
        contactInfo={{
          phone: "(785) 966-3060",
          email: "taxcommission@pbpnation.org",
          department: "Prairie Band Potawatomi Tax Commission"
        }}
      >
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="font-semibold text-green-800 mb-2">Important Notes:</h3>
          <ul className="text-sm text-green-700 space-y-1">
            <li>• The report covers both cigarette packs and RYO tobacco inventory</li>
            <li>• All calculations have been verified and submitted</li>
            <li>• Keep this PDF for your records and audit purposes</li>
            <li>• Contact us if you need to make any corrections to submitted data</li>
          </ul>
        </div>
      </SuccessPage>
    );
  }

  if (loadingDistributors) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#840028] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading distributors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 bg-white">
      {/* Header */}
      <div className="text-center mb-6 sm:mb-8 border-b-2 border-[#840028] pb-4 sm:pb-6">
        <h1 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#840028]">Monthly Retailer Tobacco Report</h1>
        <div className="font-bold mb-2 text-[#840028] text-sm sm:text-base">
          <div>Prairie Band Potawatomi Tax Commission</div>
          <div>16281 Q Road</div>
          <div>Mayetta, KS 66509</div>
        </div>
        <div className="italic mb-2 text-[#840028] text-sm sm:text-base">
          Reports and Payment due on the 10th of each Month
        </div>
      </div>

      {/* Form ID */}
      <div className="bg-gray-100 px-4 sm:px-6 py-2 border-b">
        <div className="flex justify-between items-center">
          <span className="font-semibold text-sm sm:text-base">Retailer Tobacco Report</span>
          <span className="text-gray-600 text-xs sm:text-sm">Page 1</span>
        </div>
      </div>

      {/* Retailer Information Section */}
      <div className="border border-gray-300 p-3 sm:p-4 mb-4 sm:mb-6">
        {/* Distributor Row - At Top */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 mb-3">
          <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">Retailer:</label>
          <div className="px-3 py-2 border border-gray-300 bg-gray-50 sm:min-w-80 text-sm sm:text-base">
            {retailerInfo.retailer}
          </div>
        </div>

        {/* Month and Year Row */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0 mb-3">
          <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">Month:</label>
          <select
            value={retailerInfo.month}
            onChange={(e) => handleRetailerInfoChange('month', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white sm:min-w-32 sm:mr-8 text-sm sm:text-base"
          >
            {monthOptions.map(month => (
              <option key={month} value={month}>{month}</option>
            ))}
          </select>

          <label className="font-bold sm:w-12 sm:mr-4 text-[#840028] text-sm sm:text-base">Year:</label>
          <select
            value={retailerInfo.year}
            onChange={(e) => handleRetailerInfoChange('year', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white sm:min-w-20 text-sm sm:text-base"
          >
            {yearOptions.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        <div className="hidden">
          <input
            type="text"
            value={retailerInfo.reportMonth}
            readOnly
          />
          <input
            type="text"
            value={retailerInfo.reportYear}
            readOnly
          />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
          <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">PBPN Tribal Distributor License Expiration Date:</label>
          <div className="px-3 py-2 border border-gray-300 bg-gray-50 sm:min-w-32 text-sm sm:text-base">
            {retailerInfo.expirationDate}
          </div>
        </div>
      </div>

      {/* Main Form Content */}
      <div className="p-4 sm:p-6">
        {/* Cigarette Packs Section */}
        <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-800">
          Tobacco Retailer - Packs of Cigarettes
        </h2>

        {/* Table Header */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-3 sm:mb-4 border-b pb-2">
          <div className="text-xs sm:text-sm font-semibold text-gray-700"></div>
          <div className="text-xs sm:text-sm font-semibold text-gray-700 text-center"># of packs</div>
          <div className="text-xs sm:text-sm font-semibold text-gray-700 text-center"># of packs</div>
        </div>

        <div className="space-y-3 sm:space-y-4">
          {/* Line 10 */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 items-center">
            <label className="text-xs sm:text-sm text-gray-700 col-span-1 sm:col-span-1">
              10. Beginning 1st of the month Inventory of Stamped cigarette packs (last month's line 17)
            </label>
            <div className="text-center font-semibold text-xs sm:text-sm">10.</div>
            <div className="relative">
              <input
                type="number"
                name="line10"
                min="0"
                value={formData.line10}
                onChange={handleInputChange}
                readOnly={!isLine10Editable}
                className={`border border-gray-300 px-2 text-right sm:px-3 py-1 sm:py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-full text-xs sm:text-sm ${!isLine10Editable ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                  }`}
                placeholder="0"
              />
              {loadingStartingCount && (
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-3 sm:h-4 w-3 sm:w-4 border-b-2 border-blue-600"></div>
                </div>
              )}
              {!isLine10Editable && !loadingStartingCount && (
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                  <svg className="h-3 sm:h-4 w-3 sm:w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
              )}
            </div>
          </div>
          {loadingStartingCount && (
            <div className="text-xs text-blue-600 text-center -mt-2">Loading starting counts...</div>
          )}
          {!isLine10Editable && !loadingStartingCount && (
            <div className="text-xs text-gray-500 text-center -mt-2">
              This value is loaded from the previous month's report and cannot be edited
            </div>
          )}

          {/* Line 11 - Calculated - DYNAMIC DESCRIPTION */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 items-center">
            <label className="text-xs sm:text-sm text-gray-700 col-span-1 sm:col-span-1">
              11. STAMPED Cigarette packs received/purchased during the month ({generateCalculationDescription('11', shipmentLocations)})
            </label>
            <div className="text-center font-semibold text-xs sm:text-sm">11.</div>
            <div className="bg-gray-100 px-2 sm:px-3 py-1 sm:py-2 rounded border text-xs sm:text-sm text-right">
              {formatNumber(calculateLine11())}
            </div>
          </div>

          {/* Dynamic Distributor Fields for Line 11 */}
          {renderDistributorFields(
            shipmentLocations,
            setShipmentLocations,
            handleShipmentNameChange,
            handleShipmentLocationChange,
            addShipmentLocation,
            removeShipmentLocation,
            'cigarette'
          )}

          {/* Line 12 - CALCULATED (line 10 + line 11) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 items-center border-t pt-3 sm:pt-4">
            <label className="text-xs sm:text-sm text-gray-700 font-semibold col-span-1 sm:col-span-1">
              12. Total STAMPED Cigarette packs available (line 10 + line 11)
            </label>
            <div className="text-center font-semibold text-xs sm:text-sm">12.</div>
            <div className="bg-gray-100 px-2 sm:px-3 py-1 sm:py-2 rounded border text-xs sm:text-sm text-right">
              {formatNumber(calculateLine12())}
            </div>
          </div>

          {/* Line 13 */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 items-center">
            <label className="text-xs sm:text-sm text-gray-700 col-span-1 sm:col-span-1">
              13. STAMPED Cigarette packs sold during the month
            </label>
            <div className="text-center font-semibold text-xs sm:text-sm">13.</div>
            <input
              type="number"
              name="line13"
              min="0"
              value={formData.line13}
              onChange={handleInputChange}
              className="border border-gray-300 px-2 text-right sm:px-3 py-1 sm:py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
              placeholder="0"
            />
          </div>

          {/* Line 14 */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 items-center">
            <label className="text-xs sm:text-sm text-gray-700 col-span-1 sm:col-span-1">
              14. STAMPED cigarette packs destroyed or returned to distributor/Tax Commission
            </label>
            <div className="text-center font-semibold text-xs sm:text-sm">14.</div>
            <input
              type="number"
              name="line14"
              min="0"
              value={formData.line14}
              onChange={handleInputChange}
              className="border border-gray-300 px-2 sm:px-3 text-right py-1 sm:py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
              placeholder="0"
            />
          </div>

          {/* Line 15 */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 items-center">
            <label className="text-xs sm:text-sm text-gray-700 col-span-1 sm:col-span-1">
              15. Un-explained variance in inventory
            </label>
            <div className="text-center font-semibold text-xs sm:text-sm">15.</div>
            <input
              type="number"
              name="line15"
              min="0"
              value={formData.line15}
              onChange={handleInputChange}
              className="border border-gray-300 px-2 sm:px-3 py-1 text-right sm:py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
              placeholder="0"
            />
          </div>

          {/* Line 16 - Calculated */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 items-center">
            <label className="text-xs sm:text-sm text-gray-700 col-span-1 sm:col-span-1">
              16. Total STAMPED Cigarette removed from inventory (Line 13 + Line 14 + Line 15)
            </label>
            <div className="text-center font-semibold text-xs sm:text-sm">16.</div>
            <div className="bg-gray-100 px-2 sm:px-3 py-1 sm:py-2 rounded border text-xs sm:text-sm text-right">
              {formatNumber(calculateLine16())}
            </div>
          </div>

          {/* Line 17 - Calculated */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 items-center border-t pt-3 sm:pt-4">
            <label className="text-xs sm:text-sm text-gray-700 font-semibold col-span-1 sm:col-span-1">
              17. End of month STAMPED cigarette packs Inventory (Line 12 - Line 16)
            </label>
            <div className="text-center font-semibold text-xs sm:text-sm">17.</div>
            <div className="bg-gray-100 px-2 sm:px-3 py-1 sm:py-2 rounded border font-semibold text-xs sm:text-sm text-right">
              {formatNumber(calculateLine17())}
            </div>
          </div>

          {/* Line 18 */}
          <div className="grid grid-cols-1 gap-3 sm:gap-4 items-center border-t pt-3 sm:pt-4">
            <div>
              <label className="text-xs sm:text-sm text-gray-700 block mb-1">
                18. Explanation of packs removed in line 14 and 15:
              </label>
              <textarea
                name="line18"
                value={formData.line18}
                onChange={handleInputChange}
                className="w-full border border-gray-300 px-2 sm:px-3 py-1 sm:py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                rows="3"
                placeholder="Enter explanation..."
              />
            </div>
          </div>
        </div>

        {/* RYO Tobacco Section */}
        <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-800 border-t pt-6 sm:pt-8 mt-6 sm:mt-8">
          Tobacco Retailer - Roll-Your-Own (RYO) Tobacco
        </h2>

        {/* RYO Table Header */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-3 sm:mb-4 border-b pb-2">
          <div className="text-xs sm:text-sm font-semibold text-gray-700"></div>
          <div className="text-xs sm:text-sm font-semibold text-gray-700 text-center">Ounces</div>
          <div className="text-xs sm:text-sm font-semibold text-gray-700 text-center">Ounces</div>
        </div>

        <div className="space-y-3 sm:space-y-4">
          {/* Line 32 */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 items-center">
            <label className="text-xs sm:text-sm text-gray-700 col-span-1 sm:col-span-1">
              32. Beginning 1st of the month Inventory of RYO tobacco ounces (last month's line 39)
            </label>
            <div className="text-center font-semibold text-xs sm:text-sm">32.</div>
            <input
              type="number"
              name="line32"
              min="0"
              value={formData.line32}
              onChange={handleInputChange}
              className="border border-gray-300 px-2 sm:px-3 py-1 sm:py-2 text-right rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
              placeholder="0"
            />
          </div>

          {/* Line 33 - Calculated - DYNAMIC DESCRIPTION */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 items-center">
            <label className="text-xs sm:text-sm text-gray-700 col-span-1 sm:col-span-1">
              33. RYO tobacco ounces received during the month ({generateCalculationDescription('33', ryoLocations)})
            </label>
            <div className="text-center font-semibold text-xs sm:text-sm">33.</div>
            <div className="bg-gray-100 px-2 sm:px-3 py-1 sm:py-2 rounded border text-xs sm:text-sm text-right">
              {formatNumber(calculateLine33())}
            </div>
          </div>

          {/* Dynamic Distributor Fields for Line 33 */}
          {renderDistributorFields(
            ryoLocations,
            setRyoLocations,
            handleRyoNameChange,
            handleRyoLocationChange,
            addRyoLocation,
            removeRyoLocation,
            'ryo'
          )}

          {/* Line 34 - Calculated */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 items-center border-t pt-3 sm:pt-4">
            <label className="text-xs sm:text-sm text-gray-700 font-semibold col-span-1 sm:col-span-1">
              34. Total RYO ounces available for sale (Lines 32 + Line 33)
            </label>
            <div className="text-center font-semibold text-xs sm:text-sm">34.</div>
            <div className="bg-gray-100 px-2 sm:px-3 py-1 sm:py-2 rounded border text-xs sm:text-sm text-right">
              {formatNumber(calculateLine34())}
            </div>
          </div>

          {/* Line 35 */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 items-center">
            <label className="text-xs sm:text-sm text-gray-700 col-span-1 sm:col-span-1">
              35. RYO ounces sold during the month
            </label>
            <div className="text-center font-semibold text-xs sm:text-sm">35.</div>
            <input
              type="number"
              name="line35"
              min="0"
              value={formData.line35}
              onChange={handleInputChange}
              className="border border-gray-300 px-2 sm:px-3 py-1 sm:py-2 rounded text-right focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
              placeholder="0"
            />
          </div>

          {/* Line 36 */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 items-center">
            <label className="text-xs sm:text-sm text-gray-700 col-span-1 sm:col-span-1">
              36. RYO ounces destroyed or returned to distributor or manufacturer
            </label>
            <div className="text-center font-semibold text-xs sm:text-sm">36.</div>
            <input
              type="number"
              name="line36"
              min="0"
              value={formData.line36}
              onChange={handleInputChange}
              className="border border-gray-300 px-2 sm:px-3 py-1 sm:py-2 rounded text-right focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
              placeholder="0"
            />
          </div>

          {/* Line 37 */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 items-center">
            <label className="text-xs sm:text-sm text-gray-700 col-span-1 sm:col-span-1">
              37. Adjustment (Please Explain)
            </label>
            <div className="text-center font-semibold text-xs sm:text-sm">37.</div>
            <input
              type="number"
              name="line37"
              min="0"
              value={formData.line37}
              onChange={handleInputChange}
              className="border border-gray-300 px-2 sm:px-3 py-1 sm:py-2 rounded text-right focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
              placeholder="0"
            />
          </div>

          {/* Line 38 - Calculated */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 items-center">
            <label className="text-xs sm:text-sm text-gray-700 col-span-1 sm:col-span-1">
              38. Total RYO ounces removed from inventory (Line 35 + 36 + 37)
            </label>
            <div className="text-center font-semibold text-xs sm:text-sm">38.</div>
            <div className="bg-gray-100 px-2 sm:px-3 py-1 sm:py-2 rounded border text-xs sm:text-sm text-right">
              {formatNumber(calculateLine38())}
            </div>
          </div>

          {/* Line 39 - Calculated */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 items-center border-t pt-3 sm:pt-4">
            <label className="text-xs sm:text-sm text-gray-700 font-semibold col-span-1 sm:col-span-1">
              39. End of month "Calculated" RYO Inventory (Line 34 - Line 38)
            </label>
            <div className="text-center font-semibold text-xs sm:text-sm">39.</div>
            <div className="bg-gray-100 px-2 sm:px-3 py-1 sm:py-2 rounded border font-semibold text-xs sm:text-sm text-right">
              {formatNumber(calculateLine39())}
            </div>
          </div>

          {/* RYO Explanation */}
          <div className="grid grid-cols-1 gap-3 sm:gap-4 items-center border-t pt-3 sm:pt-4">
            <div>
              <label className="text-xs sm:text-sm text-gray-700 block mb-1">
                Explanation of RYO ounces removed in line 36:
              </label>
              <textarea
                name="line39Explanation"
                value={formData.line39Explanation}
                onChange={handleInputChange}
                className="w-full border border-gray-300 px-2 sm:px-3 py-1 sm:py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                rows="3"
                placeholder="Enter explanation..."
              />
            </div>
          </div>
        </div>

        {/* Conditionally render Tobacco Revenue Section */}
        {showTobaccoRevenue && (
          <div className="border border-gray-300 p-4 sm:p-6 mt-6 sm:mt-8 rounded-lg">
            <h3 className="text-lg font-bold mb-4 text-center text-gray-800">Tobacco Revenue</h3>

            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold text-gray-700"></th>
                    <th className="border border-gray-300 px-3 py-2 text-right text-sm font-semibold text-gray-700">Revenue Amount</th>
                    <th className="border border-gray-300 px-3 py-2 text-right text-sm font-semibold text-gray-700">Tax Rate</th>
                    <th className="border border-gray-300 px-3 py-2 text-right text-sm font-semibold text-gray-700">Tax Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Row 1: Gift Shop - Tobacco Revenue */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 text-sm text-gray-700">
                      1. Gift Shop - Tobacco Revenue
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-sm text-right">
                      <div className="flex items-center justify-end">
                        <span className="mr-1">$</span>
                        <input
                          type="text"
                          value={formData.tobaccoRevenue.giftShop}
                          onChange={(e) => handleTobaccoRevenueChange('giftShop', e.target.value)}
                          className="w-32 px-2 py-1 border border-gray-300 rounded text-right focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="0.00"
                        />
                      </div>
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-sm text-right text-gray-700">
                      6%
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-sm text-right text-gray-700 font-semibold">
                      ${calculateTaxAmount(formData.tobaccoRevenue.giftShop)}
                    </td>
                  </tr>

                  {/* Row 2: Nation Station - Tobacco Revenue */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 text-sm text-gray-700">
                      2. Nation Station - Tobacco Revenue
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-sm text-right">
                      <div className="flex items-center justify-end">
                        <span className="mr-1">$</span>
                        <input
                          type="text"
                          value={formData.tobaccoRevenue.nationStation}
                          onChange={(e) => handleTobaccoRevenueChange('nationStation', e.target.value)}
                          className="w-32 px-2 py-1 border border-gray-300 rounded text-right focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="0.00"
                        />
                      </div>
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-sm text-right text-gray-700">
                      6%
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-sm text-right text-gray-700 font-semibold">
                      ${calculateTaxAmount(formData.tobaccoRevenue.nationStation)}
                    </td>
                  </tr>

                  {/* Row 3: Vending - Tobacco Revenue */}
                  <tr>
                    <td className="border border-gray-300 px-3 py-2 text-sm text-gray-700">
                      3. Vending - Tobacco Revenue
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-sm text-right">
                      <div className="flex items-center justify-end">
                        <span className="mr-1">$</span>
                        <input
                          type="text"
                          value={formData.tobaccoRevenue.vending}
                          onChange={(e) => handleTobaccoRevenueChange('vending', e.target.value)}
                          className="w-32 px-2 py-1 border border-gray-300 rounded text-right focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="0.00"
                        />
                      </div>
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-sm text-right text-gray-700">
                      6%
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-sm text-right text-gray-700 font-semibold">
                      ${calculateTaxAmount(formData.tobaccoRevenue.vending)}
                    </td>
                  </tr>

                  {/* Total Row */}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="border border-gray-300 px-3 py-2 text-sm text-gray-700">
                      Total Tobacco
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-sm text-right text-gray-700">
                      ${formatAmount(calculateTobaccoTotals().totalRevenue.toFixed(2))}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-sm text-right text-gray-700">
                      6%
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-sm text-right text-gray-700">
                      ${formatAmount(calculateTobaccoTotals().totalTax.toFixed(2))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Supporting Documents (Optional) - Same as StampsInventory */}
        <div className="border border-gray-300 p-4 sm:p-6 mb-4 sm:mb-6 mt-6 sm:mt-8 rounded-lg bg-white">
          <h3 className="text-lg font-bold mb-3 sm:mb-4 text-center text-[#840028]">
            Supporting Documents (Optional)
          </h3>
          <p className="text-sm text-gray-700 mb-3 text-center">
            You may upload one or more files to support this report. This step is optional.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Files:
            </label>
            <input
              type="file"
              multiple
              onChange={handleAttachmentChange}
              className="block w-full text-sm text-gray-700
                         file:mr-4 file:py-2 file:px-4
                         file:rounded-md file:border-0
                         file:text-sm file:font-semibold
                         file:bg-[#840028] file:text-white
                         hover:file:bg-[#6a0020]"
            />
            {attachments && attachments.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-medium text-gray-700 mb-1">Selected files:</p>
                <ul className="space-y-2 text-xs text-gray-700">
                  {attachments.map((file, index) => (
                    <li
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded px-2 py-1"
                    >
                      <span className="truncate mr-2">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(index)}
                        className="text-red-600 hover:text-red-800 text-[11px] font-semibold"
                      >
                        Delete
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* UPDATED: Signature Section - Matching UnstampedPacks format */}
        <div className="border border-gray-300 p-4 sm:p-6 mt-6 sm:mt-8 rounded-lg">
          <h3 className="text-lg font-bold mb-3 sm:mb-4 text-center text-gray-800">Signature</h3>

          <div className="mb-4">
            <label className="font-bold block mb-2 text-gray-700 text-sm sm:text-base">
              Sign below to certify the accuracy of this report:
            </label>

            {/* Signature Canvas Container */}
            <div
              id="signature-container-packs"
              className="border-2 border-gray-300 rounded-lg p-3 sm:p-4 bg-white"
              style={{ minHeight: '200px' }}
            >
              <SignatureCanvas
                ref={sigCanvas}
                penColor="#0000FF"
                backgroundColor="rgb(255, 255, 255)"
                canvasProps={{
                  className: 'signature-canvas w-full h-full border border-gray-400 rounded bg-white cursor-crosshair',
                  style: {
                    width: '100%',
                    height: '100%',
                    minHeight: '150px'
                  }
                }}
                onBegin={handleSignatureBegin}
                onEnd={handleSignatureEnd}
                clearOnResize={false}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
            <div className="text-sm text-gray-600 text-center sm:text-left">
              {isSigned ? (
                <span className="text-green-600 font-bold">✓ Signature provided</span>
              ) : (
                <span className="text-red-600">Please sign above</span>
              )}
            </div>

            <button
              type="button"
              onClick={clearSignature}
              className="px-4 py-2 bg-[#840028] text-white rounded hover:bg-[#6a0020] transition duration-200 text-sm w-full sm:w-auto"
            >
              Clear Signature
            </button>
          </div>

          {/* Printed Name, Title, and Date Fields - Added */}
          <div className="border-t pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Printed Name: <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={printedName}
                  onChange={(e) => setPrintedName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Enter your full name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title: <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="Enter your title"
                  required
                />
              </div>
            </div>

            {/* Date Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Signed:
              </label>
              <input
                type="text"
                value={currentDate}
                readOnly
                className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 font-medium text-gray-700 cursor-not-allowed text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                This date is automatically set to today's date and cannot be modified
              </p>
            </div>
          </div>
        </div>

        {/* Certification */}
        <div className="border-t-2 border-b-2 border-gray-500 py-4 sm:py-6 my-6 sm:my-8 text-center italic text-gray-700 text-xs sm:text-sm">
          <p>
            I certify that I am a duly authorized owner, partner, director, officer or representative of the licensee and that the statements made herein are true and correct to the best of my knowledge.
          </p>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t">
        <div className="flex justify-center">
          <button
            onClick={handleSubmit}
            disabled={!isSigned || submitting || !printedName.trim() || !title.trim()}
            className="bg-[#840028] hover:bg-[#6a0020] text-white font-bold py-3 px-6 sm:px-8 rounded-lg transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed w-full sm:w-auto text-sm sm:text-base"
          >
            {submitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting...
              </span>
            ) : (
              isSigned && printedName.trim() && title.trim() ? 'Submit Report' : 'Please Complete All Required Fields'
            )}
          </button>
        </div>
      </div>

      {/* Custom CSS for signature canvas */}
      <style jsx>{`
        .signature-canvas {
          cursor: crosshair !important;
          touch-action: none;
        }
        .signature-canvas:active {
          cursor: grabbing !important;
        }
        #signature-container-packs {
          min-height: 200px;
        }
        
        @media (max-width: 640px) {
          #signature-container-packs {
            min-height: 150px;
          }
        }
      `}</style>
    </div>
  );
};

export default PacksInventory;